import { useState, useRef, useEffect } from "react";
import {
  Input,
  Progress,
  IconButton,
  Flex as ChakraFlex,
  useBreakpointValue,
} from "@chakra-ui/react";
import SearchIcon from "components/icons/SearchIcon";
import CloseIcon from "components/icons/CloseIcon";
import {
  MotionBox as Box,
  MotionFlex as Flex,
  viewAppearDefault,
} from "utils/motion";
import { useFormatMessage } from "components/base/Trans";

const quickFade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { type: "tween", duration: 0.1 } },
};

export default function SearchField({ onChange, loading, value }) {
  const formatMessage = useFormatMessage();
  const [isExpandedState, setIsExpanded] = useState(false);
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: "md" },
  );
  const isExpanded = isMobile || isExpandedState;
  const inputRef = useRef(null);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      // Timeout to allow animation to complete before focusing
      const timer = setTimeout(() => {
        inputRef.current.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  const handleChange = (event) => {
    onChange(event.target.value);
  };

  const handleClear = () => {
    onChange("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const toggleSearch = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Flex position="relative" direction="column">
      <Flex
        w="100%"
        justify="start"
        variants={viewAppearDefault}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
      >
        <ChakraFlex align="center" w={isExpanded ? "100%" : "auto"}>
          <IconButton
            aria-label={formatMessage({ id: "LBL_BLOG_SEARCH_ICON" })}
            icon={<SearchIcon />}
            onClick={toggleSearch}
            variant="ghost"
            size="md"
            borderBottom={isExpanded ? "1px solid" : "0px solid"}
            borderColor="gray.300"
            transition="border-bottom 0.2s ease-in-out"
          />
          <Box
            as={ChakraFlex}
            alignItems="center"
            position="relative"
            minWidth={0}
            minHeight="40px"
            initial="hidden"
            animate={isExpanded ? "visible" : "hidden"}
            variants={{
              hidden: { opacity: 0, width: "0px" },
              visible: { opacity: 1, width: "100%" },
            }}
          >
            <Input
              ref={inputRef}
              value={value}
              onChange={handleChange}
              color="black.900"
              placeholder={
                isExpanded ? formatMessage({ id: "LBL_BLOG_SEARCH" }) : ""
              }
              opacity={isExpanded ? 1 : 0}
              variant="flushed"
              borderColor="gray.300"
              height="40px"
              paddingBottom="0px"
              _hover={{ borderColor: "gray.300" }}
              _focus={{ borderColor: "gray.300", boxShadow: `none` }}
              pr={value && isExpanded ? "2.5rem" : "0"}
              w="100%"
              minW={["100%", null, "320px"]}
              autoComplete="off"
            />
            {isExpanded && value && (
              <IconButton
                aria-label={formatMessage({ id: "LBL_CLEAR_SEARCH" })}
                icon={<CloseIcon />}
                onClick={handleClear}
                variant="ghost"
                size="sm"
                color="gray.600"
                position="absolute"
                right="0.5rem"
                top="50%"
                transform="translateY(-50%)"
                zIndex="1"
                _hover={{ color: "black.900" }}
              />
            )}
          </Box>
        </ChakraFlex>
      </Flex>
      <Box
        bottom="0px"
        w="100%"
        position="absolute"
        variants={quickFade}
        initial="hidden"
        animate={loading ? "visible" : "hidden"}
      >
        <Progress h="2px" isIndeterminate colorScheme="green" />
      </Box>
    </Flex>
  );
}
